import { useAppSelector } from '../../lib/hooks';
import { MyReservationViewRow, useGetMyReservationsQuery } from '../../lib/model/kantoordag/kantoordagMyReservationsStreamingApi';
import { DateTime } from 'luxon';
import { QueryResult } from '../../components/Alerts.tsx';
import { useTranslation } from 'react-i18next';
import 'react-calendar/dist/Calendar.css';
import { FaCalendarDay, FaCar, FaParking } from 'react-icons/fa';
import { Card, Spinner } from "flowbite-react";
import ProtectedRoute from "../../components/ProtectedRoute.tsx";
import { ROOM__NO_ROOM } from "../Constants.tsx";
import { GiOfficeChair } from "react-icons/gi";
import { FaBuilding } from "react-icons/fa6";

const RESERVATIONS_LIMIT = 5;

export default function Reservations() {
    const { t } = useTranslation("reservations");
    const user = useAppSelector(state => state.user.user);
    const userId = user?.email;
    const todayIso = DateTime.now().toISODate();

    const myReservationsQuery = useGetMyReservationsQuery(
        { userId: userId || '', fromDate: todayIso || '', limit: RESERVATIONS_LIMIT },
        { skip: !userId || !todayIso }
    );

    const reservationsByDate = myReservationsQuery.data?.reservations.reduce((acc, reservation) => {
        const date = DateTime.fromISO(reservation.date).toISODate();
        if (date) {
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(reservation);
        }
        return acc;
    }, {} as Record<string, MyReservationViewRow[]>);

    return (
        <ProtectedRoute authenticated={true}>
            <h1>{t('upcoming_reservations')}</h1>
            <Card>
                <QueryResult query={myReservationsQuery} message={t("loading_failed")}>
                    {!myReservationsQuery.data?.ready ? (
                        <Spinner aria-label={t("loading_reservations")}/>
                    ) : reservationsByDate && Object.keys(reservationsByDate).length > 0 ? (
                        <div>
                            <ul>
                                {Object.entries(reservationsByDate)
                                    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB)) // Sort dates
                                    .map(([date, reservations]) => (
                                        reservations.map((res, index) => (
                                            <li key={`${date}-${index}`} className="flex items-center" data-testid={`reservation-${date}`}>
                                                {/* Date part - fixed width */}
                                                <span className="flex items-center w-65"> {/* Using w-65 for fixed width */}
                                                    <FaCalendarDay className="mr-1"/>
                                                    <span className="text-base">
                                                        {DateTime.fromISO(date).toLocaleString({
                                                            weekday: 'long',
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric'
                                                        })}
                                                    </span>
                                                </span>

                                                {/* Conditional rendering for the first separator and reservation details */}
                                                {(res.room !== ROOM__NO_ROOM || res.requestedParkingspot) && (
                                                    <>
                                                        <span className="mx-2 text-gray-400">|</span> {/* Separator between date and details */}
                                                        <span className="flex items-center flex-grow"> {/* Reservation details container */}

                                                            {res.room === ROOM__NO_ROOM ? (
                                                                <span className="flex items-center w-60">
                                                                    <FaBuilding className="mr-2 text-gray-600"/>{res.building}
                                                                </span>
                                                            ) : (
                                                                <span className="flex items-center w-60">
                                                                    <GiOfficeChair className="mr-1 text-gray-600" title={t("room_reservation")}/>{res.room}
                                                                </span>
                                                            )}
                                                            {res.requestedParkingspot && (
                                                                <span className="flex items-center">
                                                                    <span className="mx-2 text-gray-400">|</span>
                                                                    {res.assignedParkingspot ? (
                                                                        <FaParking className="text-blue-500" title={t("parking_assigned")}/>
                                                                    ) : (
                                                                        <FaCar className="text-gray-500" title={t("parking_reservation")}/>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </>
                                                )}
                                            </li>
                                        ))
                                    ))}
                            </ul>
                        </div>
                    ) : (
                        <p className="text-gray-500 italic">{t("no_upcoming_reservations")}</p>
                    )}
                </QueryResult>
            </Card>
        </ProtectedRoute>
    );
};
